<template>
  <div class="scanner-interface">

    <!-- Header -->
    <div class="scanner-header">
      <v-icon dark small class="mr-2">{{ modeIcon }}</v-icon>
      <span class="scanner-title">{{ modeLabel }}</span>
      <v-spacer></v-spacer>
      <v-btn icon dark @click="session.signOut()">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </div>

    <!-- Main content -->
    <div class="scanner-body">

      <!-- Scan input -->
      <div class="scanner-buffer-wrapper">
        <input
          ref="capture"
          class="scanner-input"
          type="text"
          inputmode="none"
          :placeholder="$t('scanner.label.scan-or-enter-barcode')"
          v-model="buffer"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          @keydown.enter.prevent="submit"
          @keydown.tab.prevent="submit"
          @blur="onBlur"
        />
      </div>

      <!-- NFC section -->
      <div class="scanner-nfc" v-if="nfcSupported">
        <v-btn
          :color="nfcActive ? 'success' : 'grey darken-1'"
          dark
          rounded
          depressed
          @click="toggleNfc"
          class="scanner-nfc-btn"
        >
          <v-icon left>mdi-nfc</v-icon>
          {{ nfcActive ? $t('scanner.action.nfc-active') : $t('scanner.action.nfc-start') }}
        </v-btn>
        <span v-if="nfcError" class="scanner-nfc-error">{{ nfcError }}</span>
      </div>

      <!-- Last scan result -->
      <transition name="scanner-fade">
        <div v-if="lastScan" class="scanner-last-scan" :key="lastScan.ts">
          <div class="scanner-last-scan-label">{{ $t('scanner.label.last-scan') }}</div>
          <div class="scanner-last-scan-value">{{ lastScan.value }}</div>
          <div class="scanner-last-scan-meta">
            <v-icon x-small dark class="mr-1">{{ lastScan.source === 'nfc' ? 'mdi-nfc' : 'mdi-barcode' }}</v-icon>
            <span>{{ lastScan.source === 'nfc' ? 'NFC' : $t('scanner.label.barcode') }}</span>
          </div>
        </div>
      </transition>

      <!-- Scan log -->
      <div class="scanner-log" v-if="scanLog.length">
        <div
          v-for="(entry, i) in scanLog"
          :key="i"
          class="scanner-log-entry"
        >
          <v-icon x-small dark class="mr-1">{{ entry.source === 'nfc' ? 'mdi-nfc' : 'mdi-barcode' }}</v-icon>
          <span class="scanner-log-value">{{ entry.value }}</span>
          <span class="scanner-log-time">{{ entry.time }}</span>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import { sessionStore } from '@/store/pinia/sessionStore'

export default {
  name: 'scannerInterface',

  setup() {
    const session = sessionStore()
    return { session }
  },

  data() {
    return {
      buffer: '',
      lastScan: null,
      scanLog: [],

      // NFC
      nfcSupported: 'NDEFReader' in window,
      nfcActive: false,
      nfcError: null,
      nfcReader: null,
      nfcAbort: null,
    }
  },

  computed: {
    modeLabel() {
      if (this.session.scanner_mode === 'storage') return this.$t('login.label.interface-storage')
      if (this.session.scanner_mode === 'pos') return this.$t('login.label.interface-pos')
      return 'Scanner'
    },
    modeIcon() {
      if (this.session.scanner_mode === 'storage') return 'mdi-warehouse'
      if (this.session.scanner_mode === 'pos') return 'mdi-cash-register'
      return 'mdi-barcode-scan'
    },
  },

  mounted() {
    this.$nextTick(() => this.$refs.capture && this.$refs.capture.focus())
  },

  beforeDestroy() {
    this.stopNfc()
  },

  methods: {

    submit() {
      const val = this.buffer.trim()
      this.buffer = ''
      if (val) this.handleBarcode(val)
    },

    onBlur() {
      setTimeout(() => this.$refs.capture && this.$refs.capture.focus(), 50)
    },

    handleBarcode(value) {
      const now = new Date()
      const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      const entry = { value, source: 'barcode', time, ts: now.getTime() }
      this.lastScan = entry
      this.scanLog.unshift(entry)
      if (this.scanLog.length > 20) this.scanLog.pop()
      this.$emit('scan', entry)
    },

    // NFC
    async toggleNfc() {
      if (this.nfcActive) {
        this.stopNfc()
      } else {
        await this.startNfc()
      }
    },

    async startNfc() {
      this.nfcError = null
      try {
        this.nfcAbort = new AbortController()
        const reader = new window.NDEFReader()
        this.nfcReader = reader
        await reader.scan({ signal: this.nfcAbort.signal })
        this.nfcActive = true
        reader.addEventListener('reading', ({ message, serialNumber }) => {
          let value = serialNumber || ''
          for (const record of message.records) {
            if (record.recordType === 'text') {
              const decoder = new TextDecoder(record.encoding || 'utf-8')
              value = decoder.decode(record.data)
              break
            }
            if (record.recordType === 'url') {
              const decoder = new TextDecoder()
              value = decoder.decode(record.data)
              break
            }
          }
          if (!value) value = serialNumber
          const now = new Date()
          const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
          const entry = { value, source: 'nfc', time, ts: now.getTime() }
          this.lastScan = entry
          this.scanLog.unshift(entry)
          if (this.scanLog.length > 20) this.scanLog.pop()
          this.$emit('scan', entry)
        })
        reader.addEventListener('readingerror', () => {
          this.nfcError = this.$t('scanner.error.nfc-read-error')
        })
      } catch (e) {
        this.nfcActive = false
        if (e.name === 'NotAllowedError') {
          this.nfcError = this.$t('scanner.error.nfc-permission-denied')
        } else {
          this.nfcError = e.message
        }
      }
    },

    stopNfc() {
      if (this.nfcAbort) {
        this.nfcAbort.abort()
        this.nfcAbort = null
      }
      this.nfcReader = null
      this.nfcActive = false
    },

  },
}
</script>

<style scoped lang="scss">

.scanner-interface {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #121212;
  color: #fff;
  overflow: hidden;
}

.scanner-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #1e1e1e;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
}

.scanner-title {
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.8;
}

.scanner-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  overflow-y: auto;
  gap: 20px;
}

.scanner-buffer-wrapper {
  width: 100%;
  max-width: 600px;
}

.scanner-input {
  width: 100%;
  padding: 16px 20px;
  background: #1e1e1e;
  border: 2px solid #333;
  border-radius: 12px;
  color: #fff;
  font-size: 1.3rem;
  font-family: monospace;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #2064AE;
  }

  &::placeholder {
    color: rgba(255,255,255,0.35);
    font-family: inherit;
    font-size: 1rem;
  }
}

.scanner-nfc {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.scanner-nfc-btn {
  min-width: 180px;
}

.scanner-nfc-error {
  font-size: 0.8rem;
  color: #ff5252;
}

.scanner-last-scan {
  width: 100%;
  max-width: 600px;
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.scanner-last-scan-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.5;
  margin-bottom: 8px;
}

.scanner-last-scan-value {
  font-size: 1.6rem;
  font-weight: 700;
  font-family: monospace;
  word-break: break-all;
  margin-bottom: 8px;
}

.scanner-last-scan-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  opacity: 0.5;
}

.scanner-log {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.scanner-log-entry {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: #1a1a1a;
  border-radius: 6px;
  font-size: 0.85rem;
  gap: 6px;
}

.scanner-log-value {
  flex: 1;
  font-family: monospace;
  opacity: 0.8;
}

.scanner-log-time {
  opacity: 0.4;
  font-size: 0.75rem;
  white-space: nowrap;
}

.scanner-fade-enter-active,
.scanner-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.scanner-fade-enter,
.scanner-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

</style>
